import React, { useState, useEffect, useMemo } from 'react'
import { ServerConfig } from 'config'
import axios from 'axios'
import { useSelector } from 'react-redux'
import { makeStyles } from '@material-ui/core/styles'
import {
  Typography,
  TextField,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Grid,
  Box,
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import CheckIcon from '@material-ui/icons/Check'
import SearchIcon from '@material-ui/icons/Search'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'

// One component, two usages:
//
// 1) Keyword picker on a support entry (deferred save via forwardRef):
//    <KeywordsManagement
//      ref={keywordsRef}
//      title="Keywords"
//      initialItems={details.keywords}
//      itemLabel="keyword"          // field used as the chip label
//      listUrl={`${ServerConfig.SERVER_URL}/admin/support/keywords`}
//      createUrl={`${ServerConfig.SERVER_URL}/admin/support/keywords`}
//      createBody={(term) => ({ keyword: term })}
//      onItemsChange={(dirty) => setStatus(s => ({ ...s, dirty: { ...s.dirty, keywords: dirty } }))}
//    />
//    On save, parent reads: keywordsRef.current.getIds()
//
// 2) Synonym manager on a keyword (immediate save, no ref needed):
//    <KeywordsManagement
//      title="Synonyms"
//      initialItems={details.synonyms}
//      itemLabel="synonym"
//      createUrl={`${ServerConfig.SERVER_URL}/admin/support/keywords/${details.id}/synonyms`}
//      createBody={(term) => ({ synonym: term })}
//      deleteUrl={`${ServerConfig.SERVER_URL}/admin/support/synonyms`}  // /:id appended
//    />
//    No listUrl = no pool search, no pager.
//    No deleteUrl on keywords = remove is local only (folded into entry PUT).

const useStyles = makeStyles((theme) => ({
  root: { padding: '10px' },
  title: { marginBottom: '8px' },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginBottom: '12px',
  },
  chip: { margin: '2px' },
  emptyText: {
    fontStyle: 'italic',
    marginBottom: '12px',
  },
  search: { width: '100%', marginBottom: '12px', backgroundColor: '#fff' },
  loading: { 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center',
    padding: '20px',
    minHeight: '300px',
  },
  gridContainer: {
    marginBottom: '12px',
  },
  gridItem: {
    display: 'flex',
  },
  row: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '8px 10px',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '4px',
    backgroundColor: theme.palette.background.paper,
  },
  rowName: { fontSize: '14px', color: theme.palette.text.primary },
  rowSub: {
    fontSize: '12px',
    color: theme.palette.text.secondary,
    marginTop: '2px',
  },
  addedButton: { color: theme.palette.secondary.main },
  createRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '12px 10px',
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: '4px',
  },
  errorText: {
    color: theme.palette.error.main,
    marginTop: '8px',
    fontSize: '13px',
  },
  pager: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    marginTop: '16px',
    flexWrap: 'wrap',
  },
  pageButton: {
    minWidth: '32px',
    fontSize: '12px',
  },
  pageLabel: { 
    fontSize: '13px',
    margin: '0 8px',
  },
  IconButton: {
    color: theme.palette.text.primary,
  },
}))

// mirrors the backend normalize_support_text so exact-match detection is consistent
const normalize = (value) =>
  (value || '').toString().toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()

const synonymText = (synonyms) =>
  (synonyms || [])
    .map((s) => (typeof s === 'string' ? s : s.synonym))
    .join(', ')

const serverMessage = (error) => {
  const data = error && error.response && error.response.data
  if (!data) return null
  return data.msg || data.message || (typeof data === 'string' ? data : null)
}

const KeywordsManagement = React.forwardRef((props, ref) => {
  const classes = useStyles()
  const auth = useSelector((state) => state.auth)

  const {
    title = 'Keywords',
    initialItems = [],
    itemLabel = 'keyword',   // field name used as chip label on each item
    listUrl = null,          // paginated pool; null = no pool (synonym mode)
    createUrl,               // POST target to create a new item
    createBody,              // (term) => body object for the POST
    deleteUrl = null,        // DELETE /:id; null = local-only remove (keyword mode)
    onItemsChange = null,    // (dirty: bool) => void
    addOnly = false,         // true = skip search pool, show plain add input (synonym mode)
  } = props

  const authConfig = () => {
    const config = { headers: { 'Content-Type': 'application/json' } }
    if (auth.access_token)
      config.headers.Authorization = `Bearer ${auth.access_token}`
    return config
  }

  const [items, setItems] = useState(initialItems)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [pages, setPages] = useState(0)
  const [pool, setPool] = useState([])
  const [loading, setLoading] = useState(false)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState('')
  const [hasChanges, setHasChanges] = useState(false)

  const attachedIds = useMemo(() => new Set(items.map((k) => k.id)), [items])

  useEffect(() => {
    setItems(initialItems)
  }, [initialItems])

  useEffect(() => { setPage(1) }, [search])

  // pool fetch -- only when listUrl is set (keyword mode)
  useEffect(() => {
    if (!listUrl) return

    let active = true
    const handle = setTimeout(async () => {
      setLoading(true)
      try {
        const term = search.trim()
        const url = term
          ? `${listUrl}/${encodeURIComponent(term)}/${page}`
          : `${listUrl}/${page}`
        const res = await axios.get(url, authConfig())
        if (active) {
          setPool(res.data.items || [])
          setPages(res.data.pages || 0)
        }
      } catch (e) {
        if (active) { setPool([]); setPages(0) }
      } finally {
        if (active) setLoading(false)
      }
    }, 250)

    return () => { active = false; clearTimeout(handle) }
  }, [search, page, listUrl])

  // show "add as new" when no pool result AND no attached item is an exact match
  const hasExactMatch = useMemo(() => {
    const t = normalize(search.trim())
    if (!t) return false
    return (
      pool.some((k) => normalize(k[itemLabel]) === t) ||
      items.some((k) => normalize(k[itemLabel]) === t)
    )
  }, [pool, items, search, itemLabel])

  const markChanged = () => {
    setHasChanges(true)
    if (onItemsChange) onItemsChange(true)
  }

  const handleAdd = (item) => {
    if (attachedIds.has(item.id)) return
    setItems((prev) => [...prev, item])
    markChanged()
  }

  const handleRemove = async (item) => {
    setError('')
    if (deleteUrl) {
      // synonym mode: DELETE immediately
      try {
        await axios.delete(`${deleteUrl}/${item.id}`, authConfig())
      } catch (e) {
        setError(serverMessage(e) || `Could not remove ${itemLabel}`)
        return
      }
    }
    // keyword mode: local remove, folded into entry PUT later
    setItems((prev) => prev.filter((k) => k.id !== item.id))
    markChanged()
  }

  const handleCreate = async () => {
    const term = search.trim()
    if (!term || creating || !createUrl) return
    setCreating(true)
    setError('')
    try {
      const res = await axios.post(createUrl, createBody(term), authConfig())
      const created = res.data
      setItems((prev) =>
        prev.some((k) => k.id === created.id) ? prev : [...prev, created]
      )
      setSearch('')
      markChanged()
    } catch (e) {
      setError(serverMessage(e) || `Could not add ${itemLabel} "${term}"`)
    } finally {
      setCreating(false)
    }
  }

  React.useImperativeHandle(ref, () => ({
    getIds: () => items.map((k) => k.id),
    hasChanges: () => hasChanges,
    resetChanges: () => setHasChanges(false),
  }))

  const term = search.trim()
  const showCreate = !!term && !hasExactMatch && !loading

  // Generate page links similar to PaginatedList (show all if ≤10, else ±5 around current)
  const getPageLinks = () => {
    const links = []
    if (pages <= 10) {
      for (let i = 1; i <= pages; i++) {
        links.push(i)
      }
    } else {
      for (let i = Math.max(1, page - 5); i <= Math.min(pages, page + 5); i++) {
        links.push(i)
      }
    }
    return links
  }

  const pageLinks = getPageLinks()

  return (
    <div className={classes.root}>
      <Typography variant="h6" className={classes.title}>
        {title}
      </Typography>

      {items.length === 0 ? (
        <Typography className={classes.emptyText}>
          No {itemLabel}s attached yet.
        </Typography>
      ) : (
        <div className={classes.chipRow}>
          {items.map((item) => (
            <Chip
              key={item.id}
              label={item[itemLabel]}
              color="secondary"
              className={classes.chip}
              onDelete={() => handleRemove(item)}
            />
          ))}
        </div>
      )}

      {addOnly ? (
        // Synonym mode: plain add input, no pool search.
        // Backend rejects duplicates (globally unique) with a named 409,
        // so no pre-search is needed -- the error message does the work.
        <div className={classes.row}>
          <TextField
            variant="outlined"
            size="small"
            placeholder={`Add a ${itemLabel}`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCreate() } }}
            style={{ flex: 1 }}
          />
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={handleCreate}
            disabled={creating || !term}
          >
            Add
          </Button>
        </div>
      ) : (
        // Keyword mode: searchable paginated pool + create-on-no-exact-match.
        <>
          <TextField
            className={classes.search}
            variant="outlined"
            size="small"
            placeholder={`Search ${itemLabel}s`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <SearchIcon fontSize="small" style={{ marginRight: 8 }} />
              ),
            }}
          />

          {listUrl && loading && (
            <div className={classes.loading}><CircularProgress size={24} /></div>
          )}

          {listUrl && !loading && pool.length > 0 && (
            <Grid container spacing={2} className={classes.gridContainer}>
              {pool.map((item) => {
                const isAttached = attachedIds.has(item.id)
                return (
                  <Grid item xs={12} sm={6} key={item.id} className={classes.gridItem}>
                    <div className={classes.row}>
                      <div style={{ flex: 1 }}>
                        <div className={classes.rowName}>{item[itemLabel]}</div>
                        {item.synonyms && item.synonyms.length > 0 && (
                          <div className={classes.rowSub}>{synonymText(item.synonyms)}</div>
                        )}
                      </div>
                      {isAttached ? (
                        <Button size="small" disabled startIcon={<CheckIcon />} className={classes.addedButton}>
                          Added
                        </Button>
                      ) : (
                        <Button size="small" startIcon={<AddIcon />} onClick={() => handleAdd(item)}>
                          Add
                        </Button>
                      )}
                    </div>
                  </Grid>
                )
              })}
            </Grid>
          )}

          {showCreate && (
            <div className={classes.createRow}>
              <Typography className={classes.emptyText}>
                No exact match for "{term}".
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={handleCreate}
                disabled={creating}
              >
                Add "{term}" as a new {itemLabel}
              </Button>
            </div>
          )}

          {/* Pagination controls at the bottom */}
          {listUrl && pages > 1 && (
            <div className={classes.pager}>
              {/* Previous button */}
              <IconButton 
                className={classes.IconButton}
                size="small" 
                disabled={page <= 1} 
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeftIcon />
              </IconButton>

              {/* Page number buttons */}
              {pageLinks.map((pageNum) => (
                <Button
                  key={pageNum}
                  size="small"
                  className={classes.pageButton}
                  // variant={pageNum === page ? 'contained' : 'outlined'}
                  onClick={() => setPage(pageNum)}
                  disabled={pageNum === page}
                >
                  {pageNum}
                </Button>
              ))}

              {/* Next button */}
              <IconButton 
                className={classes.IconButton}
                size="small"
                disabled={page >= pages} 
                onClick={() => setPage((p) => Math.min(pages, p + 1))}
              >
                <ChevronRightIcon />
              </IconButton>

              {/* Page info */}
              <span className={classes.pageLabel}>
                Page {page} of {pages}
              </span>
            </div>
          )}
        </>
      )}

      {error && (
        <Typography className={classes.errorText}>{error}</Typography>
      )}
    </div>
  )
})

export default KeywordsManagement